import { cn } from '@/lib/utils';
import type { MaImage } from '@/lib/zybooks-api';
import { Check } from 'lucide-react';
import { useState } from 'react';
import { getMaImageUrl } from './image-library-utils';
import { ImageOptionsDropdown } from './image-options-dropdown';

export const ImageMasonryCard = ({
    data,
    onEdit,
    onEditAltText,
    onDelete,
    onPreview,
    onPick,
    pickerSelected,
}: {
    data: MaImage;
    onEdit?: (image: MaImage) => void;
    onEditAltText?: (image: MaImage) => void;
    onDelete?: (image: MaImage) => void;
    onPreview?: (image: MaImage) => void;
    onPick?: (image: MaImage) => void;
    pickerSelected?: MaImage | null;
}) => {
    const [isHovering, setIsHovering] = useState(false);
    const [isOptionsMenuOpen, setIsOptionsMenuOpen] = useState(false);
    const isImageSelected = Boolean(onPick && pickerSelected != null && pickerSelected === data);
    const showBottomOverlay = isHovering || isOptionsMenuOpen || isImageSelected;

    return (
        <div
            className={cn('relative flex min-h-[100px] items-center justify-center', onPick ? 'cursor-pointer' : undefined)}
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
            onClick={() => (onPick ? onPick(data) : onPreview?.(data))}
        >
            <img
                className="block max-w-full shadow-[0_8px_16px_-4px_rgba(0,0,0,0.18)]"
                src={getMaImageUrl(data)}
                alt={data.alt_text ?? ''}
                loading="lazy"
            />
            {isImageSelected ? (
                <div
                    className="absolute left-2 top-2 flex size-9 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-md ring-2 ring-background"
                    aria-hidden
                >
                    <Check
                        className="size-5"
                        strokeWidth={3}
                    />
                </div>
            ) : null}
            <div
                className={cn(
                    'absolute inset-x-0 bottom-0 flex items-center px-2',
                    'bg-gradient-to-t from-black/80 via-black/50 to-transparent',
                    showBottomOverlay ? 'opacity-100' : 'opacity-0 pointer-events-none',
                )}
            >
                <h2 className="min-w-0 flex-1 truncate text-sm font-medium text-white">{data.filename ?? ''}</h2>
                {onEditAltText ? (
                    <div className="shrink-0 text-white">
                        <ImageOptionsDropdown
                            image={data}
                            onEditAltText={() => onEditAltText(data)}
                            onEdit={onEdit ? () => onEdit(data) : undefined}
                            onDelete={onDelete ? () => onDelete(data) : undefined}
                            onOpenChange={setIsOptionsMenuOpen}
                        />
                    </div>
                ) : null}
            </div>
        </div>
    );
};
